import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { Bank, Branch, BankAccount } from '@/types';
import { fetchAllBanks, fetchAllBranches } from '@/utils/bank';
import BankSelectField from './BankSelect';
import BranchSelectField from './BranchSelect';

interface BankAccountFormProps {
  // isSubmitting で二重送信を止めるため、Promise を返すことを型で担保する
  onSubmit: (data: BankAccount) => Promise<void>;
  initialData?: BankAccount;
  serverErrors?: string[];
  submitLabel: string;
}

export type BankOption = {
  value: string;
  label: string;
  bank: Bank;
};

export type BranchOption = {
  value: string;
  label: string;
  branch: Branch;
};

const BankAccountForm: React.FC<BankAccountFormProps> = ({
  onSubmit,
  initialData,
  serverErrors = [],
  submitLabel,
}) => {
  const {
    register,
    handleSubmit,
    setValue,
    control,
    formState: { errors, isSubmitted, isSubmitting },
  } = useFormContext<BankAccount>();

  const [bankCode, setBankCode] = useState(initialData?.bankCode ?? '');
  const [branchCode, setBranchCode] = useState(initialData?.branchCode ?? '');
  const [allBanks, setAllBanks] = useState<Bank[]>([]);
  const [allBranches, setAllBranches] = useState<Branch[]>([]);
  const [branchesBankCode, setBranchesBankCode] = useState<string | null>(null);
  const [bankFetchError, setBankFetchError] = useState<string | null>(null);
  const [branchFetchError, setBranchFetchError] = useState<string | null>(null);
  const serverErrorRef = useRef<HTMLDivElement>(null);

  // 送信ボタンはフォーム最下部にあり、エラー欄は最上部。気付かず再送信されるのを防ぐ
  useEffect(() => {
    if (serverErrors.length === 0) return;

    serverErrorRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });
    serverErrorRef.current?.focus();
  }, [serverErrors]);

  useEffect(() => {
    const controller = new AbortController();

    const fetchBanks = async () => {
      try {
        const banks = await fetchAllBanks(controller.signal);
        setAllBanks(banks);
      } catch (error) {
        if (controller.signal.aborted) return;

        console.error(error);
        setBankFetchError(
          '金融機関の一覧を取得できませんでした。時間をおいて再読み込みしてください'
        );
      }
    };
    fetchBanks();

    return () => controller.abort();
  }, []);

  useEffect(() => {
    if (!initialData) return;

    setValue('bankName', initialData.bankName);
    setValue('bankCode', initialData.bankCode);
    setValue('branchName', initialData.branchName);
    setValue('branchCode', initialData.branchCode);
    setValue('accountType', initialData.accountType);
    setValue('accountNumber', initialData.accountNumber);
    setValue('accountHolderName', initialData.accountHolderName);

    setBankCode(initialData.bankCode ?? '');
    setBranchCode(initialData.branchCode ?? '');
  }, [initialData, setValue]);

  const bankOptions: BankOption[] = useMemo(
    () =>
      allBanks.map((bank) => ({
        value: bank.code,
        // 「◯◯信金銀行」とならないように suffix を付けない
        label: bank.name,
        bank,
      })),
    [allBanks]
  );

  const branchOptions: BranchOption[] = useMemo(
    () =>
      allBranches.map((branch) => ({
        value: branch.code,
        label: branch.name,
        branch,
      })),
    [allBranches]
  );

  // 選択中の銀行コードに対応する支店一覧をロード
  const loadBranches = async () => {
    if (!bankCode || bankCode.length !== 4) return;
    if (branchesBankCode === bankCode && allBranches.length > 0) return;

    try {
      const branches = await fetchAllBranches(bankCode);
      setAllBranches(branches);
      setBranchesBankCode(bankCode);
      setBranchFetchError(null);
    } catch (error) {
      console.error(error);
      setBranchFetchError(
        '支店の一覧を取得できませんでした。時間をおいて再度お試しください'
      );
    }
  };

  const handleBankChange = (
    option: BankOption | null,
    fieldOnChange: (value: string) => void
  ) => {
    const newBankName = option?.bank.name ?? '';
    const newBankCode = option?.value ?? '';

    fieldOnChange(newBankName);

    // ローカル state と他フィールドを更新
    setBankCode(newBankCode);
    setValue('bankCode', newBankCode, { shouldValidate: true });

    // 銀行が変わったら支店情報はリセット
    setBranchCode('');
    setValue('branchName', '');
    setValue('branchCode', '');

    setAllBranches([]);
    setBranchesBankCode(null);
    setBranchFetchError(null);
  };

  const handleBranchChange = (
    option: BranchOption | null,
    fieldOnChange: (value: string) => void
  ) => {
    const newBranchName = option?.branch.name ?? '';
    const newBranchCode = option?.value ?? '';

    fieldOnChange(newBranchName);

    setBranchCode(newBranchCode);
    setValue('branchName', newBranchName, { shouldValidate: true });
    setValue('branchCode', newBranchCode, { shouldValidate: true });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className='progress-bar w-100'>
        <div className='bar h-100 w-75 bg-black'></div>
      </div>
      <div className='p-3'>
        {serverErrors.length > 0 && (
          <div
            className='alert alert-danger'
            ref={serverErrorRef}
            role='alert'
            tabIndex={-1}
          >
            <ul>
              {serverErrors.map((message, index) => (
                <li key={`${index}-${message}`}>{message}</li>
              ))}
            </ul>
          </div>
        )}

        {[bankFetchError, branchFetchError].map(
          (message) =>
            message && (
              <div key={message} className='alert alert-warning' role='alert'>
                {message}
              </div>
            )
        )}

        {isSubmitted && Object.keys(errors).length > 0 && (
          <div className='alert alert-danger'>
            <ul>
              {errors.bankName && <li>{errors.bankName.message as string}</li>}
              {errors.bankCode && <li>{errors.bankCode.message as string}</li>}
              {errors.branchName && (
                <li>{errors.branchName.message as string}</li>
              )}
              {errors.branchCode && (
                <li>{errors.branchCode.message as string}</li>
              )}
              {errors.accountType && (
                <li>{errors.accountType.message as string}</li>
              )}
              {errors.accountNumber && (
                <li>{errors.accountNumber.message as string}</li>
              )}
              {errors.accountHolderName && (
                <li>{errors.accountHolderName.message as string}</li>
              )}
            </ul>
          </div>
        )}

        {/* 金融機関（react-select） */}
        <div className='mb-2'>
          <p className='small mb-1 text-black'>金融機関</p>
          <BankSelectField
            control={control}
            bankOptions={bankOptions}
            initialBankName={initialData?.bankName}
            onBankChange={handleBankChange}
          />
        </div>

        <div className='mb-2'>
          <p className='small mb-0 text-black'>金融機関コード</p>
          <p className='small text-muted mb-1'>自動反映されます</p>
          <input
            type='text'
            className='form-control w-100'
            value={bankCode}
            {...register('bankCode', {
              required: '金融機関コードは必須です',
              pattern: {
                value: /^\d{4}$/,
                message: '4桁の半角数字を入力してください',
              },
            })}
            readOnly
            disabled
            style={{
              backgroundColor: '#e9ecef', // 薄い灰色
              cursor: 'not-allowed',
            }}
          />
        </div>

        {/* 支店名（react-select） */}
        <div className='mb-2'>
          <p className='small mb-0 text-black'>支店名</p>
          <BranchSelectField
            control={control}
            branchOptions={branchOptions}
            initialBranchName={initialData?.branchName}
            onBranchChange={handleBranchChange}
            loadBranches={loadBranches}
          />
        </div>

        <div className='mb-2'>
          <p className='small mb-0 text-black'>支店コード</p>
          <p className='small text-muted mb-1'>自動反映されます</p>
          <input
            type='text'
            className='form-control w-100'
            value={branchCode}
            readOnly
            {...register('branchCode', {
              required: '支店コードは必須です',
              pattern: {
                value: /^\d{3}$/,
                message: '3桁の半角数字を入力してください',
              },
            })}
            disabled
            style={{
              backgroundColor: '#e9ecef',
              cursor: 'not-allowed',
            }}
          />
        </div>

        <div className='mb-2'>
          <p className='small mb-1 text-black'>預金種目</p>
          <select
            id='accountType'
            className='form-select w-100'
            {...register('accountType', { required: '預金種目は必須です' })}
          >
            <option value='checking'>普通</option>
            <option value='current'>当座</option>
          </select>
        </div>

        <div className='mb-2'>
          <p className='small mb-1 text-black'>口座番号</p>
          <input
            type='text'
            className='form-control w-100'
            {...register('accountNumber', {
              required: '口座番号は必須です',
              pattern: {
                value: /^\d{1,7}$/,
                message: '7桁以内の半角数字を入力してください',
              },
            })}
          />
        </div>

        <div className='mb-2'>
          <p className='small mb-1 text-black'>口座名義(ｶﾀｶﾅ)</p>
          <input
            type='text'
            className='form-control w-100'
            placeholder='ｴﾌﾞﾘﾀﾛｳ'
            {...register('accountHolderName', {
              required: '口座名義は必須です',
              pattern: {
                value: /^[ｦ-ﾟ .,()]+$/,
                message: '半角カタカナ、半角スペース、記号（.,()）のみで入力してください',
              },
            })}
          />
        </div>

        {/* 送信中の再送信を防ぐ */}
        <button
          type='submit'
          className='btn btn-primary w-100'
          disabled={isSubmitting}
          aria-busy={isSubmitting}
        >
          {isSubmitting ? '送信中...' : submitLabel}
        </button>
      </div>
    </form>
  );
};

export default BankAccountForm;
